// src/utils/docHistory.js
// Saves a history of generated documents (title, tool, date, preview)
// Used by all 4 tools + shown in Dashboard history panel
import { supabase } from '../lib/supabaseClient.js';
import { userKey } from './storageHelper.js';

// Lazy key getter — evaluated at call time so userKey() resolves to the logged-in user.
// (Previous bug: module-level const HISTORY_KEY = userKey(...) was evaluated at import,
//  before login, so it always resolved to "guest:qais_doc_history" and history saves
//  went to the wrong key.)
const getHistoryKey = () => userKey('qais_doc_history');
const MAX_HISTORY = 50; // keep last 50 documents

export async function saveDocToHistory({ tool, title, preview, framework }) {
  const entry = {
    id: `doc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    tool,        // 'document_generator' | 'eu_classifier' | 'risk_calculator' | 'mapper'
    title,       // e.g. "AI Policy — Acme Corp"
    preview,     // first 120 chars of content
    framework,   // e.g. 'ISO 42001' | 'EU AI Act' | 'All Frameworks'
    generatedAt: new Date().toISOString(),
  };

  // Save to localStorage immediately
  try {
    const key = getHistoryKey();
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const updated = [entry, ...existing].slice(0, MAX_HISTORY);
    localStorage.setItem(key, JSON.stringify(updated));
  } catch {}

  // Sync to Supabase
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { data } = await supabase
        .from('user_profiles')
        .select('doc_history')
        .eq('user_id', session.user.id)
        .single();
      const existing = data?.doc_history || [];
      const updated = [entry, ...existing].slice(0, MAX_HISTORY);
      await supabase.from('user_profiles').upsert(
        { user_id: session.user.id, doc_history: updated, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
    }
  } catch {}

  return entry;
}

export async function loadDocHistory() {
  // Try Supabase first
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { data } = await supabase
        .from('user_profiles')
        .select('doc_history')
        .eq('user_id', session.user.id)
        .single();
      if (data?.doc_history?.length) {
        localStorage.setItem(getHistoryKey(), JSON.stringify(data.doc_history));
        return data.doc_history;
      }
    }
  } catch {}
  // Fallback to localStorage
  try {
    return JSON.parse(localStorage.getItem(getHistoryKey()) || '[]');
  } catch {
    return [];
  }
}

export function getDocHistory() {
  try {
    return JSON.parse(localStorage.getItem(getHistoryKey()) || '[]');
  } catch {
    return [];
  }
}

// Tool display config
export const TOOL_CONFIG = {
  document_generator: { label: 'Document Generator', icon: '📄', color: '#3b82f6' },
  eu_classifier:      { label: 'EU AI Classifier',   icon: '🇪🇺', color: '#8b5cf6' },
  risk_calculator:    { label: 'Risk Calculator',    icon: '⚖️',  color: '#f59e0b' },
  mapper:             { label: 'Cross Framework Mapper', icon: '🔗', color: '#06b6d4' },
};